import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import {
  TransactionResponse,
  GetTransactionsResponse,
  CalendarEvent,
} from "../types/transactions";
import { useApp } from "./AppContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import { upsertTransactionInList } from "../utils/transactionLookup";
import moment from "moment";

interface TransactionContextType {
  transactions: GetTransactionsResponse[];
  selectedTransaction: GetTransactionsResponse | null;
  isLoading: boolean;
  error: string | null;
  fetchTransactions: (
    salonId: string,
    params?: string,
  ) => Promise<GetTransactionsResponse[] | undefined>;
  // Sales tab (BE-1). Kept separate from fetchTransactions so the Sales list
  // never overwrites the calendar's shared `transactions` state.
  salesTransactions: GetTransactionsResponse[];
  salesLoading: boolean;
  salesError: string | null;
  fetchSales: (
    businessLocationId: string,
    view?: "transactions" | "purchases",
    params?: string,
  ) => Promise<GetTransactionsResponse[] | undefined>;
  fetchTransaction: (
    transactionId: string,
  ) => Promise<TransactionResponse | null>;
  refreshTransactions: () => Promise<void>;
  setSelectedTransaction: (transaction: GetTransactionsResponse | null) => void;
  addTransaction: (transaction: GetTransactionsResponse) => void;
  clearError: () => void;
  deleteLineItem: (lineItemUuid: string) => Promise<any>;
  updateTransactionInList: (
    updatedTransaction: GetTransactionsResponse,
  ) => void;
  /** Replace the transaction with the same id, or append it if absent. */
  upsertTransaction: (transaction: GetTransactionsResponse) => void;
}

const TransactionContext = createContext<TransactionContextType | undefined>(
  undefined,
);

interface TransactionProviderProps {
  children: ReactNode;
}

export function TransactionProvider({ children }: TransactionProviderProps) {
  const [transactions, setTransactions] = useState<GetTransactionsResponse[]>(
    [],
  );
  const [selectedTransaction, setSelectedTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchParams, setLastFetchParams] = useState<{
    salonId: string;
    params?: string;
  } | null>(null);

  const [salesTransactions, setSalesTransactions] = useState<
    GetTransactionsResponse[]
  >([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState<string | null>(null);

  const { getTransactions, getSales, getTransaction, fetchWithAuth } = useApp();

  const fetchTransactions = useCallback(
    async (salonId: string, params?: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getTransactions(salonId, params);
        if (response) {
          setTransactions(response.transactions);
          setLastFetchParams({ salonId, params });
          return response.transactions;
        } else {
          setTransactions([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch transactions",
        );
        setTransactions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [getTransactions],
  );

  const fetchSales = useCallback(
    async (
      businessLocationId: string,
      view: "transactions" | "purchases" = "transactions",
      params?: string,
    ) => {
      try {
        setSalesLoading(true);
        setSalesError(null);

        const response = await getSales(businessLocationId, view, params);
        // Tolerate the new sales envelope ({ view, sales: [...] }), a bare
        // array, and the legacy fallback ({ transactions: [...] }).
        const rows: GetTransactionsResponse[] = Array.isArray(response)
          ? response
          : (response?.sales ?? response?.transactions ?? []);
        setSalesTransactions(rows);
        return rows;
      } catch (err) {
        setSalesError(
          err instanceof Error ? err.message : "Failed to fetch sales",
        );
        setSalesTransactions([]);
        return undefined;
      } finally {
        setSalesLoading(false);
      }
    },
    [getSales],
  );

  const fetchTransaction = useCallback(
    async (transactionId: string): Promise<TransactionResponse | null> => {
      try {
        setError(null);
        const response = await getTransaction(transactionId);
        return response;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch transaction",
        );
        return null;
      }
    },
    [getTransaction],
  );

  const refreshTransactions = useCallback(async () => {
    if (lastFetchParams) {
      await fetchTransactions(lastFetchParams.salonId, lastFetchParams.params);
    }
  }, [fetchTransactions, lastFetchParams]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const addTransaction = useCallback((transaction: GetTransactionsResponse) => {
    setTransactions((prevTransactions) => [...prevTransactions, transaction]);
  }, []);

  const deleteLineItem = useCallback(
    async (lineItemUuid: string) => {
      try {
        const responseJson = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.LINE_ITEM_DELETE, "", {
            lineItemUuid,
          }),
          {
            method: "DELETE",
          },
        );

        return responseJson;
      } catch (error) {
        console.error("Error deleting line item:", error);
        throw error;
      }
    },
    [fetchWithAuth],
  );

  const updateTransactionInList = useCallback(
    (updatedTransaction: GetTransactionsResponse) => {
      setTransactions((prevTransactions) => {
        return prevTransactions.map((transaction) => {
          if (
            transaction.transaction.id === updatedTransaction.transaction.id
          ) {
            return updatedTransaction;
          }
          return transaction;
        });
      });
    },
    [],
  );

  const upsertTransaction = useCallback(
    (transaction: GetTransactionsResponse) => {
      setTransactions((prev) => upsertTransactionInList(prev, transaction));
    },
    [],
  );

  // [LOOP] churn probe
  useValueChurnProbe("TransactionContext", { transactions, isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: TransactionContextType = useMemo(
    () => ({
      transactions,
      selectedTransaction,
      isLoading,
      error,
      fetchTransactions,
      salesTransactions,
      salesLoading,
      salesError,
      fetchSales,
      fetchTransaction,
      refreshTransactions,
      setSelectedTransaction,
      addTransaction,
      clearError,
      deleteLineItem,
      updateTransactionInList,
      upsertTransaction,
    }),
    [
      transactions,
      selectedTransaction,
      isLoading,
      error,
      fetchTransactions,
      salesTransactions,
      salesLoading,
      salesError,
      fetchSales,
      fetchTransaction,
      refreshTransactions,
      addTransaction,
      clearError,
      deleteLineItem,
      updateTransactionInList,
      upsertTransaction,
    ],
  );

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
}

export function useTransaction() {
  const context = useContext(TransactionContext);
  if (context === undefined) {
    throw new Error("useTransaction must be used within a TransactionProvider");
  }
  return context;
}
